import React from 'react';
import { ArrowLeft, Shield, Eye, Database, Mail, Cookie } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

interface PrivacyPageProps {
  language: 'de' | 'en';
  onBack: () => void;
}

export const PrivacyPage: React.FC<PrivacyPageProps> = ({ language, onBack }) => {
  const content = {
    de: {
      title: 'Datenschutzerklärung',
      backButton: 'Zurück zur Hauptseite',
      lastUpdated: 'Zuletzt aktualisiert: Juli 2025',
      intro: 'List Immobilien nimmt den Schutz Ihrer persönlichen Daten sehr ernst. Diese Datenschutzerklärung informiert Sie über die Art, den Umfang und den Zweck der Erhebung und Verwendung personenbezogener Daten auf unserer Website.',
      
      sections: [
        {
          title: '1. Verantwortlicher',
          icon: Shield,
          content: `Verantwortlich für die Datenverarbeitung auf dieser Website ist:

List Immobilien GmbH
Geschäftsführer: Franz List
Gesellschafter: Franz List, Mag. Dr. Gloria Sultano
Firmenbuchnummer: FN 157176 t
Firmenbuchgericht: Handelsgericht Wien
UID: ATU53193609
Falls Sie Fragen zum Datenschutz haben, können Sie sich jederzeit an uns wenden:
E-Mail: office@listimmobilien.at`
        },
        {
          title: '2. Erhebung und Speicherung personenbezogener Daten',
          icon: Database,
          content: `Beim Besuch unserer Website werden automatisch Informationen allgemeiner Natur erfasst:
          
• IP-Adresse
• Datum und Uhrzeit des Zugriffs
• Zeitzonendifferenz zur Greenwich Mean Time (GMT)
• Inhalt der Anforderung (konkrete Seite)
• Zugriffsstatus/HTTP-Statuscode
• Übertragene Datenmenge
• Website, von der die Anforderung kommt
• Browser, Betriebssystem und dessen Oberfläche
• Sprache und Version der Browsersoftware

Diese Daten werden nur zur Sicherstellung eines störungsfreien Betriebs der Website und zur Verbesserung unseres Angebots verwendet. Die Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).

Die Daten werden automatisch gelöscht, sobald sie für den Zweck der Erhebung nicht mehr erforderlich sind, spätestens jedoch nach 30 Tagen.`
        },
        {
          title: '3. Kontaktformular',
          icon: Mail,
          content: `Wenn Sie unser Kontaktformular nutzen, erheben wir folgende Daten:

• Name (Pflichtfeld)
• E-Mail-Adresse (Pflichtfeld)
• Telefonnummer (optional)
• Betreff (Pflichtfeld)
• Kategorie der Anfrage (optional)
• Nachrichteninhalt (Pflichtfeld)
• Zeitpunkt der Übermittlung

Die Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung) bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).

Die Daten werden ausschließlich für die Bearbeitung Ihrer Anfrage verwendet und nach Abschluss der Bearbeitung gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen (max. 3 Jahre).

Das Kontaktformular verwendet reCAPTCHA von Google zum Schutz vor Spam. Hierfür ist Ihre Zustimmung zu funktionalen Cookies erforderlich. Ohne diese Zustimmung ist das Kontaktformular nicht funktionsfähig.`
        },
        {
          title: '4. Cookies und Tracking-Technologien',
          icon: Cookie,
          content: `Unsere Website verwendet Cookies in verschiedenen Kategorien:

**Notwendige Cookies (immer aktiv):**
Diese Cookies sind für die grundlegende Funktionalität der Website erforderlich:
• Session-Cookies für die Navigation
• Sicherheits-Cookies
• Cookie-Consent-Status

**Funktionale Cookies (mit Zustimmung):**
Diese ermöglichen erweiterte Funktionen:
• Barrierefreiheitseinstellungen (Schriftgröße, Kontrast, etc.)
• Spracheinstellungen
• reCAPTCHA für Spam-Schutz

**Analyse-Cookies (mit Zustimmung):**
Zur Verbesserung unserer Website verwenden wir:
• Google Analytics zur anonymen Nutzungsanalyse
• Performance-Monitoring

**Marketing-Cookies (mit Zustimmung):**
Derzeit nicht aktiv, aber vorgesehen für:
• Zielgerichtete Werbung
• Social Media Integration

Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) für alle nicht-notwendigen Cookies.

Sie können Ihre Cookie-Einstellungen jederzeit über das Cookie-Banner oder den Link im Footer anpassen.`
        },
        {
          title: '5. Externe Dienste',
          icon: Shield,
          content: `Wir nutzen folgende externe Dienste:

**Google reCAPTCHA:**
• Anbieter: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland
• Zweck: Spam-Schutz im Kontaktformular
• Datenübertragung: IP-Adresse, Browser-Daten
• Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
• Datenschutz: https://policies.google.com/privacy

**Formspree (Kontaktformular-Service):**
• Anbieter: Formspree Inc., USA
• Zweck: Verarbeitung von Kontaktanfragen
• Datenübertragung: Formulardaten
• Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)

**Google Analytics (falls aktiviert):**
• Anbieter: Google Ireland Limited
• Zweck: Website-Analyse
• Datenübertragung: Anonymisierte Nutzungsdaten
• Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)`
        },
        {
          title: '6. Ihre Rechte nach der DSGVO',
          icon: Eye,
          content: `Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:

**Art. 15 DSGVO - Auskunftsrecht:**
Sie können Auskunft über die von uns verarbeiteten personenbezogenen Daten verlangen.

**Art. 16 DSGVO - Berichtigungsrecht:**
Sie können die Korrektur unrichtiger oder unvollständiger Daten verlangen.

**Art. 17 DSGVO - Löschungsrecht ("Recht auf Vergessenwerden"):**
Sie können die Löschung Ihrer personenbezogenen Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.

**Art. 18 DSGVO - Recht auf Einschränkung der Verarbeitung:**
Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen.

**Art. 20 DSGVO - Recht auf Datenübertragbarkeit:**
Sie können die Übertragung Ihrer Daten in einem strukturierten Format verlangen.

**Art. 21 DSGVO - Widerspruchsrecht:**
Sie können der Verarbeitung Ihrer Daten widersprechen, sofern diese auf Art. 6 Abs. 1 lit. f DSGVO beruht.

**Art. 7 Abs. 3 DSGVO - Widerruf der Einwilligung:**
Sie können erteilte Einwilligungen jederzeit für die Zukunft widerrufen.

**Art. 77 DSGVO - Beschwerderecht:**
Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren.

Für die Ausübung Ihrer Rechte wenden Sie sich bitte an:
E-Mail: office@listimmobilien.at`
        },
        {
          title: '7. Datensicherheit',
          icon: Shield,
          content: `Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz Ihrer Daten:

**Technische Maßnahmen:**
• SSL/TLS-Verschlüsselung für alle Datenübertragungen
• Sichere Server-Infrastruktur
• Regelmäßige Sicherheitsupdates
• Firewall-Schutz

**Organisatorische Maßnahmen:**
• Zugriffskontrollen und Berechtigungskonzepte
• Mitarbeiterschulungen zum Datenschutz
• Dokumentation der Verarbeitungstätigkeiten
• Incident-Response-Verfahren

**Aufbewahrungsfristen:**
• Kontaktanfragen: 3 Jahre nach Abschluss der Bearbeitung
• Log-Dateien: 30 Tage
• Cookie-Einstellungen: Bis zum Widerruf
• Barrierefreiheitseinstellungen: Bis zum Widerruf`
        }
      ],
      
      gdprCompliance: 'Diese Datenschutzerklärung entspricht den Anforderungen der EU-Datenschutz-Grundverordnung (DSGVO) und dem österreichischen Datenschutzgesetz (DSG).',
      contact: 'Bei Fragen zum Datenschutz wenden Sie sich bitte an:',
      authority: 'Zuständige Aufsichtsbehörde: Österreichische Datenschutzbehörde (www.dsb.gv.at)'
    },
    en: {
      title: 'Privacy Policy',
      backButton: 'Back to Main Page',
      lastUpdated: 'Last updated: July 2025',
      intro: 'List Immobilien takes the protection of your personal data very seriously. This privacy policy informs you about the nature, scope and purpose of the collection and use of personal data on our website.',
      
      sections: [
        {
          title: '1. Data Controller',
          icon: Shield,
          content: `The data controller for data processing on this website is:

List Immobilien GmbH
Managing Director: Franz List
Partners: Franz List, Mag. Dr. Gloria Sultano
Company Registration: FN 157176 t
Court of Registration: Handelsgericht Wien
VAT Number: ATU53193609

If you have any questions about data protection, you can contact us at any time:
Email: office@listimmobilien.at`
        },
        {
          title: '2. Collection and Storage of Personal Data',
          icon: Database,
          content: `When visiting our website, information of a general nature is automatically collected:

• IP address
• Date and time of access
• Time zone difference to Greenwich Mean Time (GMT)
• Content of the request (specific page)
• Access status/HTTP status code
• Amount of data transferred
• Website from which the request comes
• Browser, operating system and interface
• Language and version of browser software

This data is only used to ensure trouble-free operation of the website and to improve our services. The legal basis for data processing is Art. 6 para. 1 lit. f GDPR (legitimate interest).

The data is automatically deleted as soon as it is no longer required for the purpose of collection, but at the latest after 30 days.`
        },
        {
          title: '3. Contact Form',
          icon: Mail,
          content: `When you use our contact form, we collect the following data:

• Name (required field)
• Email address (required field)
• Phone number (optional)
• Subject (required field)
• Inquiry category (optional)
• Message content (required field)
• Time of submission

The legal basis for processing is Art. 6 para. 1 lit. b GDPR (contract initiation) or Art. 6 para. 1 lit. f GDPR (legitimate interest).

The data is used exclusively for processing your request and is deleted after processing is complete, unless there are legal retention obligations (max. 3 years).

The contact form uses Google reCAPTCHA for spam protection. This requires your consent to functional cookies. Without this consent, the contact form is not functional.`
        },
        {
          title: '4. Cookies and Tracking Technologies',
          icon: Cookie,
          content: `Our website uses cookies in various categories:

**Necessary Cookies (always active):**
These cookies are required for basic website functionality:
• Session cookies for navigation
• Security cookies
• Cookie consent status

**Functional Cookies (with consent):**
These enable advanced features:
• Accessibility settings (font size, contrast, etc.)
• Language settings
• reCAPTCHA for spam protection

**Analytics Cookies (with consent):**
To improve our website, we use:
• Google Analytics for anonymous usage analysis
• Performance monitoring

**Marketing Cookies (with consent):**
Currently not active, but planned for:
• Targeted advertising
• Social media integration

The legal basis is Art. 6 para. 1 lit. a GDPR (consent) for all non-necessary cookies.

You can adjust your cookie settings at any time via the cookie banner or the link in the footer.`
        },
        {
          title: '5. External Services',
          icon: Shield,
          content: `We use the following external services:

**Google reCAPTCHA:**
• Provider: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland
• Purpose: Spam protection in contact form
• Data transfer: IP address, browser data
• Legal basis: Art. 6 para. 1 lit. a GDPR (consent)
• Privacy: https://policies.google.com/privacy

**Formspree (Contact Form Service):**
• Provider: Formspree Inc., USA
• Purpose: Processing contact requests
• Data transfer: Form data
• Legal basis: Art. 6 para. 1 lit. b GDPR (contract fulfillment)

**Google Analytics (if activated):**
• Provider: Google Ireland Limited
• Purpose: Website analysis
• Data transfer: Anonymized usage data
• Legal basis: Art. 6 para. 1 lit. a GDPR (consent)`
        },
        {
          title: '6. Your Rights under GDPR',
          icon: Eye,
          content: `You have the following rights regarding your personal data:

**Art. 15 GDPR - Right to information:**
You can request information about the personal data we process.

**Art. 16 GDPR - Right to rectification:**
You can request correction of incorrect or incomplete data.

**Art. 17 GDPR - Right to erasure ("Right to be forgotten"):**
You can request deletion of your personal data, unless there are legal retention obligations.

**Art. 18 GDPR - Right to restriction of processing:**
You can request restriction of processing of your data.

**Art. 20 GDPR - Right to data portability:**
You can request transfer of your data in a structured format.

**Art. 21 GDPR - Right to object:**
You can object to the processing of your data if it is based on Art. 6 para. 1 lit. f GDPR.

**Art. 7 para. 3 GDPR - Withdrawal of consent:**
You can withdraw given consents at any time for the future.

**Art. 77 GDPR - Right to lodge a complaint:**
You have the right to lodge a complaint with a supervisory authority.

To exercise your rights, please contact:
Email: office@listimmobilien.at`
        },
        {
          title: '7. Data Security',
          icon: Shield,
          content: `We take appropriate technical and organizational measures to protect your data:

**Technical Measures:**
• SSL/TLS encryption for all data transfers
• Secure server infrastructure
• Regular security updates
• Firewall protection

**Organizational Measures:**
• Access controls and authorization concepts
• Employee training on data protection
• Documentation of processing activities
• Incident response procedures

**Retention Periods:**
• Contact requests: 3 years after completion of processing
• Log files: 30 days
• Cookie settings: Until revocation
• Accessibility settings: Until revocation`
        }
      ],
      
      gdprCompliance: 'This privacy policy complies with the requirements of the EU General Data Protection Regulation (GDPR) and the Austrian Data Protection Act (DSG).',
      contact: 'For questions about data protection, please contact:',
      authority: 'Competent supervisory authority: Austrian Data Protection Authority (www.dsb.gv.at)'
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="inline-flex items-center text-brand-beige hover:text-brand-dark transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            {currentContent.backButton}
          </button>
        </div>

        <ScrollReveal>
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 text-gray-900">
              {currentContent.title}
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              {currentContent.lastUpdated}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
              {currentContent.intro}
            </p>
          </div>
        </ScrollReveal>

        <div className="space-y-12">
          {currentContent.sections.map((section, index) => (
            <ScrollReveal key={index} delay={index * 0.1}>
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="p-3 bg-brand-beige/10 rounded-lg">
                    <section.icon className="w-6 h-6 text-brand-beige" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-gray max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed bg-gray-50 p-4 rounded-lg">
                        {section.content}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={0.8}>
          <div className="mt-16 p-8 bg-gray-50 rounded-2xl border border-gray-200">
            <div className="text-center">
              <Shield className="w-12 h-12 text-brand-beige mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900 mb-4">
                {currentContent.gdprCompliance}
              </p>
              <p className="text-gray-700 mb-6">
                {currentContent.contact}
              </p>
              <div className="text-brand-beige font-medium space-y-2">
                <p>office@listimmobilien.at</p>
                <p className="text-sm text-gray-600 mt-4">
                  {currentContent.authority}
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};
